/**
 * "What do you do here?" — a list, with a way out.
 *
 * A free-text box asking for a job title gets you "cfo", "C.F.O.", "المدير
 * المالي" and "finance" for the same person, which makes the field useless for
 * anything but display. A closed list gets you a shrug from the operations lead
 * of a poultry importer. So: the titles a small business in the Gulf actually
 * has, in the order they occur, and "Other" as a real option that opens a box —
 * chosen deliberately rather than defaulted into.
 *
 * Both languages come from one list. The value STORED is always the English
 * label, so a title set in Arabic still reads sensibly to a colleague working
 * in English, and the display flips with the interface.
 */

import { useEffect, useState } from 'react';
import { Input } from '@/components/shadcn/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/shadcn/select';
import { useI18n } from '@/contexts/I18nContext';

const OTHER = '__other__';

/** Grouped, because eighteen flat options is a wall. */
const GROUPS: { labelEn: string; labelAr: string; items: [string, string][] }[] = [
  {
    labelEn: 'Leadership',
    labelAr: 'الإدارة العليا',
    items: [
      ['Owner', 'مالك'],
      ['Founder', 'مؤسس'],
      ['Chief Executive Officer', 'الرئيس التنفيذي'],
      ['Managing Director', 'العضو المنتدب'],
      ['General Manager', 'المدير العام'],
      ['Partner', 'شريك'],
    ],
  },
  {
    labelEn: 'Finance',
    labelAr: 'المالية',
    items: [
      ['Chief Financial Officer', 'المدير المالي التنفيذي'],
      ['Finance Manager', 'مدير مالي'],
      ['Financial Controller', 'المراقب المالي'],
      ['Accountant', 'محاسب'],
      ['Auditor', 'مدقق حسابات'],
    ],
  },
  {
    labelEn: 'Operations',
    labelAr: 'العمليات',
    items: [
      ['Chief Operating Officer', 'مدير العمليات التنفيذي'],
      ['Operations Manager', 'مدير العمليات'],
      ['Project Manager', 'مدير مشاريع'],
      ['Procurement Manager', 'مدير المشتريات'],
      ['Warehouse Manager', 'مدير المستودع'],
      ['Branch Manager', 'مدير فرع'],
    ],
  },
  {
    labelEn: 'Commercial',
    labelAr: 'التجاري',
    items: [
      ['Sales Manager', 'مدير المبيعات'],
      ['Business Development Manager', 'مدير تطوير الأعمال'],
      ['Marketing Manager', 'مدير التسويق'],
      ['Account Manager', 'مدير حسابات العملاء'],
      ['Customer Service Manager', 'مدير خدمة العملاء'],
    ],
  },
  {
    labelEn: 'People & technology',
    labelAr: 'الموارد البشرية والتقنية',
    items: [
      ['HR Manager', 'مدير الموارد البشرية'],
      ['Office Manager', 'مدير المكتب'],
      ['Administrator', 'إداري'],
      ['IT Manager', 'مدير تقنية المعلومات'],
      ['Engineer', 'مهندس'],
      ['Consultant', 'مستشار'],
    ],
  },
];

const ALL = GROUPS.flatMap((g) => g.items.map(([en]) => en));

export function TitleField({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const { t, lang } = useI18n();
  const ar = lang === 'ar';

  /**
   * A stored title that is not in the list is somebody's own words — start in
   * "Other" with their text intact rather than silently discarding it.
   */
  const [custom, setCustom] = useState(() => (value && !ALL.includes(value) ? value : ''));
  const [isOther, setIsOther] = useState(() => !!value && !ALL.includes(value));

  useEffect(() => {
    if (value && !ALL.includes(value)) {
      setIsOther(true);
      setCustom(value);
    }
  }, [value]);

  const arabicOf = (en: string) =>
    GROUPS.flatMap((g) => g.items).find(([e]) => e === en)?.[1] ?? en;

  return (
    <div className="grid gap-2">
      <Select
        value={isOther ? OTHER : value || undefined}
        onValueChange={(v) => {
          if (v === OTHER) {
            setIsOther(true);
            onChange(custom);
          } else {
            setIsOther(false);
            onChange(v);
          }
        }}
      >
        <SelectTrigger id={id} className="w-full">
          <SelectValue placeholder={t('title_placeholder')} />
        </SelectTrigger>
        <SelectContent>
          {GROUPS.map((g) => (
            <SelectGroup key={g.labelEn}>
              <SelectLabel>{ar ? g.labelAr : g.labelEn}</SelectLabel>
              {g.items.map(([en, arLabel]) => (
                <SelectItem key={en} value={en}>
                  {ar ? arLabel : en}
                </SelectItem>
              ))}
            </SelectGroup>
          ))}
          <SelectGroup>
            <SelectItem value={OTHER}>{t('title_other')}</SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>

      {isOther ? (
        <Input
          autoFocus
          value={custom}
          placeholder={t('title_other_placeholder')}
          onChange={(e) => {
            setCustom(e.target.value);
            onChange(e.target.value);
          }}
        />
      ) : null}

      {/* The stored value is English; in Arabic show what was actually saved so
          a colleague on the other language sees the same person's title. */}
      {ar && !isOther && value && ALL.includes(value) ? (
        <p className="text-[11px] text-muted-foreground">
          {value} · {arabicOf(value)}
        </p>
      ) : null}
    </div>
  );
}
